import React, { useState, useEffect } from "react";
import { Card, CircularProgress, Box, Grid } from "@mui/material";
import VuiBox from "components/VuiBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import RegionCard from "./components/RegionCard/RegionCard";
import PieChart from "layouts/national-dashboard/components/Totals/PieChart";
import VerticalStackedBarChart from "layouts/national-dashboard/components/Totals/VerticalStackedBarChart";
import LineChart from "examples/Charts/LineCharts/LineChart";
import { lineChartOptionsDashboard } from "layouts/national-dashboard/data/lineChartOptions";
import axios from "axios";
import VuiTypography from "components/VuiTypography";
import AreaPpmSummary from "layouts/national-dashboard/components/AreaPpmSummary";

function NationalDashboard() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lineChartData, setLineChartData] = useState([]);
  const [userSites, setUserSites] = useState([]); // Added state

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch regions
        const regionsResponse = await fetch(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/regions",
          {
            method: "GET",
            headers: { Authorization: `${token}` },
          }
        );
        const regionsData = await regionsResponse.json();

        // Fetch all sites
        const sitesResponse = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/sites",
          {
            headers: { Authorization: `${token}`, "Content-Type": "application/json" },
            params: { user_id: userId },
          }
        );
        const sites = sitesResponse.data;

        setUserSites(sites); // Save sites for PPM component

        // Fetch site data
        const sitesWithData = await Promise.all(
          sites.map(async (site) => {
            try {
              const gatewayResponse = await fetch(
                `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/gateway/profile?site_id=${site.site_id}`,
                { headers: { Authorization: `${token}` } }
              );
              const gatewayData = await gatewayResponse.json();
              const gatewayName = gatewayData[0]?.name;

              if (!gatewayName) return null;

              const lambdaResponse = await axios.get(
                "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream",
                {
                  headers: { Authorization: `${token}` },
                  params: {
                    thing_name: gatewayName,
                    query_type: "area_dashboard",
                  },
                }
              );

              const { daily, rap_total_month, bit_total_month } = lambdaResponse.data;

              return {
                site_id: site.site_id,
                region_id: site.region_id,
                Asphalt_Day: parseFloat(daily[0] || 0),
                RAP_Total_Day: parseFloat(daily[1] || 0),
                Bit_Total: parseFloat(daily[2] || 0),
                Fuel_Total: parseFloat(daily[3] || 0),
                RAP_Total: parseFloat(daily[4] || 0),
                Asphalt_Year: parseFloat(daily[5] || 0),
                Breakdown_Hrs: parseFloat(daily[6] || 0),
                Offline_Hrs: parseFloat(daily[7] || 0),
                Idle_Hrs: parseFloat(daily[8] || 0),
                Production_Hrs: parseFloat(daily[9] || 0),
                Electric_Total: parseFloat(daily[10] || 0),
                Gas_Total: parseFloat(daily[11] || 0),
                RAP_Total_Month: rap_total_month || Array(12).fill(0),
                Bit_Total_Month: bit_total_month || Array(12).fill(0),
              };
            } catch (error) {
              console.error(`Error fetching data for site ${site.site_id}:`, error);
              return null;
            }
          })
        );

        const successfulSites = sitesWithData.filter((site) => site !== null);

        // Enrich regions with RAP & Asphalt data
        const regionsWithData = regionsData.map((region) => {
          const regionSites = successfulSites.filter((site) => site.region_id === region.region_id);

          return {
            ...region,
            Asphalt_Year: regionSites.reduce((sum, site) => sum + (site.Asphalt_Year || 0), 0),
            RAP_Total_Month:
              regionSites.length > 0
                ? regionSites[0].RAP_Total_Month.map((_, monthIndex) =>
                    regionSites.reduce(
                      (sum, site) => sum + (site.RAP_Total_Month[monthIndex] || 0),
                      0
                    )
                  )
                : Array(12).fill(0),
            Bit_Total_Month:
              regionSites.length > 0
                ? regionSites[0].Bit_Total_Month.map((_, monthIndex) =>
                    regionSites.reduce(
                      (sum, site) => sum + (site.Bit_Total_Month[monthIndex] || 0),
                      0
                    )
                  )
                : Array(12).fill(0),
          };
        });

        // Line chart (Binder)
        setLineChartData(
          regionsWithData.map((region) => ({
            name: region.name,
            data: region.Bit_Total_Month || Array(12).fill(0),
          }))
        );

        setRegions(regionsWithData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </VuiBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox>
        <Grid container spacing={0.5} mb={0.5}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Card sx={{ height: "100%" }}>
              <VuiBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <VuiTypography variant="h6" color="white">
                  Asphalt
                </VuiTypography>
                <PieChart
                  data={regions.map((region) => ({
                    name: region.name,
                    Asphalt_Year: region.Asphalt_Year || 0,
                  }))}
                />
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Card sx={{ height: "100%" }}>
              <VuiBox
                sx={{
                  height: "300px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <VuiTypography variant="h6" color="white">
                  RAP
                </VuiTypography>
                <VerticalStackedBarChart
                  regionData={regions.map((region) => ({
                    name: region.name,
                    RAP_Total_Month: region.RAP_Total_Month || Array(12).fill(0),
                  }))}
                />
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={3}>
            <Card sx={{ height: "100%" }}>
              <VuiBox
                sx={{
                  height: "370px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <VuiTypography variant="h6" color="white">
                  Binder
                </VuiTypography>
                <VuiBox sx={{ flexGrow: 1 }}>
                  <LineChart
                    lineChartData={lineChartData}
                    lineChartOptions={lineChartOptionsDashboard}
                  />
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={3}>
            <Card sx={{ height: "100%" }}>
              <VuiBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <VuiTypography variant="h6" color="white">
                  PPM
                </VuiTypography>
                <AreaPpmSummary userSites={userSites} />
              </VuiBox>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {regions.map((region, index) => {
            const isLast = index === regions.length - 1;
            const isOnlyInRowLg = regions.length % 3 === 1 && index === regions.length - 1;
            const isOnlyInRowMd = regions.length % 2 === 1 && index === regions.length - 1;

            return (
              <Grid
                item
                xs={12}
                sm={isOnlyInRowMd ? 12 : 6}
                md={isOnlyInRowMd ? 12 : 6}
                lg={isOnlyInRowLg ? 12 : 4}
                key={region.region_id}
              >
                <RegionCard region={region} />
              </Grid>
            );
          })}
        </Grid>
      </VuiBox>
    </DashboardLayout>
  );
}

export default NationalDashboard;
